<template>
  <div class="waste-visualization">
    <div class="space-y-4">
      <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
        <h2 class="text-xl font-bold mb-2">♻️ Mesure du Gaspillage</h2>
        <p class="text-sm opacity-90">Pesée par catégorie du menu du jour</p>
      </div>

      <!-- Message si aucun menu n'a été importé pour la journée -->
      <div v-if="noMenu" class="bg-blue-50 border-2 border-blue-300 rounded-xl p-4">
        <p class="font-semibold text-gray-800 mb-1">ℹ️ Aucun menu importé pour cette date</p>
        <p class="text-sm text-gray-600">
          Les catégories par défaut (entrée / plat / dessert) sont proposées. Importez le menu du jour pour peser
          exactement les plats servis.
        </p>
      </div>

      <!-- Poids par catégorie du menu -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">⚖️ Poids gaspillé par catégorie (kg)</h3>
        <div v-if="loading" class="text-center text-gray-500 p-4">Chargement du menu…</div>
        <div v-else class="space-y-3">
          <div
            v-for="cat in wasteCategories"
            :key="cat.category"
            class="border-2 rounded-lg p-3"
            :class="cat.weightKg > 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-200'"
          >
            <div class="flex items-center justify-between gap-4">
              <span class="font-semibold text-gray-700 flex-1">
                {{ cat.icon }} {{ cat.label }}
                <span v-if="cat.name" class="text-sm text-gray-500">— {{ cat.name }}</span>
              </span>
              <div class="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  v-model.number="cat.weightKg"
                  class="weight-input"
                  :class="cat.weightKg > 0 ? 'weight-input--active' : ''"
                  placeholder="0.00"
                  :aria-label="`Poids gaspillé pour ${cat.label}`"
                />
                <span class="text-sm text-gray-500">kg</span>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4 bg-red-50 rounded-lg p-4 text-center border-2 border-red-300">
          <p class="text-sm text-gray-600 mb-1">Total gaspillé</p>
          <p class="text-4xl font-bold text-red-600">{{ totalWaste }} kg</p>
        </div>
      </div>

      <!-- Commentaire libre -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <label for="waste-notes" class="font-bold text-gray-800 mb-3 block">📝 Commentaire (facultatif)</label>
        <textarea
          id="waste-notes"
          v-model="notes"
          class="notes-input"
          rows="3"
          placeholder="Remarques sur le service, causes du gaspillage…"
        ></textarea>
      </div>

      <!-- Boutons d'action -->
      <div class="grid grid-cols-2 gap-3">
        <button
          @click="emitResult('display')"
          :disabled="parseFloat(totalWaste) === 0"
          :class="parseFloat(totalWaste) > 0 ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          👀 Voir le résultat
        </button>
        <button
          @click="emitResult('poster')"
          :disabled="parseFloat(totalWaste) === 0"
          :class="parseFloat(totalWaste) > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          🖨️ Préparer affiche
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Icône associée à chaque catégorie de menu (MenuItem.Category côté backend).
const CATEGORY_ICONS = {
  entree: "🥗",
  plat: "🍽️",
  accompagnement: "🥔",
  produit_laitier: "🧀",
  dessert: "🍮",
  sans_viande: "🥦",
}

// Catégories de repli lorsqu'aucun menu n'a été importé pour la date.
const DEFAULT_CATEGORIES = [
  { category: "entree", label: "Entrée" },
  { category: "plat", label: "Plat" },
  { category: "dessert", label: "Dessert" },
]

export default {
  name: "WasteVisualizationMenu",
  props: {
    // Cantine dont on veut charger le menu du jour.
    canteenId: {
      type: Number,
      default: null,
    },
    // Date du repas (YYYY-MM-DD) dont les catégories de menu servent de barème.
    mealDate: {
      type: String,
      default: null,
    },
    // Catégories pré-remplies lors de l'édition d'une mesure du jour déjà saisie (CT-02-03).
    // Format attendu : [{ category, weightKg }] (name/label/icon proviennent du menu).
    initialCategories: {
      type: Array,
      default: () => [],
    },
    // Commentaire pré-rempli lors de l'édition d'une mesure existante.
    initialNotes: {
      type: String,
      default: "",
    },
  },
  emits: ["waste-saved"],
  data() {
    return {
      wasteCategories: [],
      notes: this.initialNotes,
      loading: true,
      noMenu: false,
    }
  },
  computed: {
    totalWaste() {
      return this.wasteCategories.reduce((acc, cat) => acc + (parseFloat(cat.weightKg) || 0), 0).toFixed(2)
    },
  },
  mounted() {
    this.loadMenu()
  },
  methods: {
    loadMenu() {
      if (!this.canteenId || !this.mealDate) {
        this.applyCategories(DEFAULT_CATEGORIES, true)
        return
      }
      this.loading = true
      this.$store
        .dispatch("fetchMenuItems", { canteenId: this.canteenId, date: this.mealDate })
        .then((items) => {
          if (items && items.length > 0) {
            // Une ligne de pesée par catégorie présente dans le menu, libellée avec le nom du plat.
            const cats = items.map((it) => ({
              category: it.category,
              label: it.categoryLabel || it.category,
              name: it.name || "",
            }))
            this.applyCategories(cats, false)
          } else {
            this.applyCategories(DEFAULT_CATEGORIES, true)
          }
        })
        .catch(() => {
          this.applyCategories(DEFAULT_CATEGORIES, true)
        })
    },

    applyCategories(cats, isFallback) {
      const categories = cats.map((c) => ({
        category: c.category,
        label: c.label,
        name: c.name || "",
        icon: CATEGORY_ICONS[c.category] || "🍴",
        weightKg: 0,
      }))
      // Pré-remplissage : on reporte le poids enregistré sur la catégorie correspondante.
      this.initialCategories.forEach((init) => {
        const cat = categories.find((c) => c.category === init.category)
        if (cat) cat.weightKg = parseFloat(init.weightKg) || 0
      })
      this.wasteCategories = categories
      this.noMenu = isFallback
      this.loading = false
    },

    emitResult(displayMode) {
      this.$emit("waste-saved", {
        type: "menu",
        displayMode,
        categories: this.wasteCategories.map((c) => ({
          category: c.category,
          name: c.name || c.label,
          weight_kg: parseFloat(c.weightKg) || 0,
        })),
        total_weight_kg: parseFloat(this.totalWaste),
        notes: this.notes,
      })
    },
  },
}
</script>

<style scoped>
.weight-input {
  width: 6rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.4rem 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  text-align: right;
  transition: border-color 0.15s;
}
.weight-input:focus {
  outline: none;
  border-color: #3b82f6;
}
.weight-input--active {
  border-color: #fdba74;
  background-color: #fff7ed;
  color: #ea580c;
}
.notes-input {
  width: 100%;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.15s;
}
.notes-input:focus {
  outline: none;
  border-color: #3b82f6;
}
.block {
  display: block;
}
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.gap-4 {
  gap: 1rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-1 {
  flex: 1 1 0%;
}
.items-center {
  align-items: center;
}
.justify-between {
  justify-content: space-between;
}
.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
}
.from-green-500 {
  --tw-gradient-from: #22c55e;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-green-600 {
  --tw-gradient-to: #16a34a;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.shadow-lg {
  box-shadow: 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.border-2 {
  border-width: 2px;
}
.border-gray-200 {
  border-color: #e5e7eb;
}
.border-orange-300 {
  border-color: #fdba74;
}
.border-blue-300 {
  border-color: #93c5fd;
}
.border-red-300 {
  border-color: #fca5a5;
}
.bg-white {
  background-color: #ffffff;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-orange-50 {
  background-color: #fff7ed;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.bg-green-500 {
  background-color: #22c55e;
}
.hover\:bg-green-600:hover {
  background-color: #16a34a;
}
.bg-gray-300 {
  background-color: #d1d5db;
}
.text-white {
  color: #ffffff;
}
.text-red-600 {
  color: #dc2626;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.p-6 {
  padding: 1.5rem;
}
.opacity-90 {
  opacity: 0.9;
}
.cursor-not-allowed {
  cursor: not-allowed;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:not(:disabled):hover {
  opacity: 0.9;
}
</style>
